import { Modal } from "@mui/material";
import "./CartItemDrawer.css";
import { useEffect, useMemo, useState } from "react";
import { PosCustomButton } from "../../../../../../../../customComponents/PosCustomButton/PosCustomButton";
import {
  getProductDetails,
  getInventoryData,
} from "../../../../../../../../config/Services";

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
const fmt = (val) =>
  val !== undefined && val !== null && val !== ""
    ? `₹${parseFloat(val).toFixed(2)}`
    : "N/A";

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return d; }
};

/* Pick first non-empty value from an images object */
const pickImage = (imagesObj) => {
  if (!imagesObj) return null;
  return (
    imagesObj.front || imagesObj.back || imagesObj.top ||
    imagesObj.bottom || imagesObj.left || imagesObj.right ||
    imagesObj.top_left || imagesObj.top_right || null
  );
};

/* continueSelling from IMS is a string "Y"/"N" (or null) */
const isSellingStopped  = (v) => v === "N" || v === false || v === "false";
const isContinueSelling = (v) => v === "Y" || v === true  || v === "true";

/* Return value or "N/A" */
const na = (val) =>
  val !== undefined && val !== null && val !== "" ? val : "N/A";

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */
const CartItemDrawer = ({ cartItemModalOpen, setCartItemModalOpen, cartItemData }) => {
  /* Cart mode = opened from a cart row (has quantity).
     Lookup mode = opened from a product card (gtin + name only). */
  const isCartMode        = cartItemData?.quantity !== undefined;
  const isBundle          = !!cartItemData?.isBundle;
  const isWeighingBarcode = useMemo(() => !!cartItemData?.quantityBySpecs, [cartItemData]);

  const locId = localStorage.getItem("locId");

  /* ── API state ── */
  const [productDetail, setProductDetail] = useState(null); // CMS response
  const [imsData,        setImsData]       = useState(null); // IMS root object
  const [batches,        setBatches]       = useState([]);
  const [loading,        setLoading]       = useState(false);
  const [apiError,       setApiError]      = useState(false);

  useEffect(() => {
    const gtin = cartItemData?.gtin;
    if (!cartItemModalOpen || !gtin || !locId) return;

    setLoading(true);
    setApiError(false);
    setProductDetail(null);
    setImsData(null);
    setBatches([]);

    Promise.allSettled([
      getProductDetails(gtin, locId),
      getInventoryData(locId, gtin),
    ]).then(([pdRes, invRes]) => {
      if (pdRes.status === "fulfilled") {
        setProductDetail(pdRes.value?.data?.data || null);
      }
      if (invRes.status === "fulfilled") {
        const root = invRes.value?.data?.data?.data;   // response → body → outer envelope → product object
        setImsData(root || null);
        const arr = root?.multipleBatchCreations;
        setBatches(Array.isArray(arr) ? arr : []);
      }
      if (pdRes.status === "rejected" && invRes.status === "rejected") {
        setApiError(true);
      }
      setLoading(false);
    });
  }, [cartItemModalOpen, cartItemData?.gtin]);

  /* ── Derived fields from CMS (productDetail) ── */
  const cmsImage      = pickImage(productDetail?.images) || pickImage(productDetail?.gs1_images);
  const productImage  = cartItemData?.productImage || cmsImage || null;
  const brand         = productDetail?.brand || imsData?.brand || null;
  const manufacturer  = productDetail?.company_detail?.manufacturer || null;
  const hsnCode       = productDetail?.hs_code || cartItemData?.hsnCode || null;
  const mainCat       = productDetail?.main_category    || imsData?.category    || cartItemData?.mainCategory || null;
  const cat1          = productDetail?.category_level_1 || imsData?.subCategory || null;
  const cat2          = productDetail?.category_level_2 || null;
  const netWeight     = productDetail?.weights_and_measures?.net_weight;
  const weightUnit    = productDetail?.weights_and_measures?.measurement_unit;
  const isLocal       = productDetail?.isLocalProduct;
  const productStatus = productDetail?.productSource?.productStatus || null;
  const description   = productDetail?.description || productDetail?.derived_description || null;
  const sellOutOfStock = productDetail?.sell_out_of_stock;

  /* GST — CMS has CESS too; values may be strings */
  const cmsIgst = productDetail?.igst != null ? parseFloat(productDetail.igst) : null;
  const cmsCgst = productDetail?.cgst != null ? parseFloat(productDetail.cgst) : null;
  const cmsSgst = productDetail?.sgst != null ? parseFloat(productDetail.sgst) : null;
  const cmsCess = productDetail?.cess != null ? parseFloat(productDetail.cess) : null;
  const imsIgst = imsData?.igst ?? null;
  const imsCgst = imsData?.cgst ?? null;
  const imsSgst = imsData?.sgst ?? null;
  const igst = cmsIgst ?? imsIgst;
  const cgst = cmsCgst ?? imsCgst;
  const sgst = cmsSgst ?? imsSgst;
  const cess = cmsCess;

  /* ── Derived fields from IMS (imsData) ── */
  const continueSelling = imsData?.continueSelling  ?? null;
  const overSold        = imsData?.overSold         ?? null;
  const sellingUnit     = cartItemData?.sellingUnit  ?? imsData?.sellingUnit      ?? productDetail?.sellingUnit ?? null;
  const packagingType   = cartItemData?.packagingType ?? imsData?.packagingType   ?? productDetail?.packaging_type ?? null;
  const unitType        = imsData?.unitType ?? null;
  const threshHold      = imsData?.threshHold      ?? null;
  const reorderPoint    = imsData?.reorderPoint    ?? null;
  const reorderQty      = imsData?.reorderQuantity ?? null;
  const reorderQtyType  = imsData?.reorderQuantityType ?? null;
  const marginValue     = imsData?.marginValue     ?? null;
  const marginType      = imsData?.marginType      ?? null;
  const marginBasedOn   = imsData?.marginBasedOn   ?? null;
  const moq             = imsData?.minimumOrderQuantity     ?? null;
  const moqUnit         = imsData?.minimumOrderQuantityUnit ?? null;
  const specification   = imsData?.specification   ?? null;

  /* ── Per-batch fallback for B2B price ── */
  const firstBatch      = batches[0];
  const b2bSellingPrice = cartItemData?.b2bSellingPrice ?? firstBatch?.b2bSellingPrice ?? null;

  /* ── Offers ── */
  const hasBatchOffer = batches.some((b) => b.haveOffer);
  const hasOffer = !!(cartItemData?.isOfferAvailable || cartItemData?.haveOffer || hasBatchOffer);
  const offerList = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const b of batches) {
      for (const o of (b?.offers?.offersList || [])) {
        const key = o?.offerName || o?.id || JSON.stringify(o);
        if (!seen.has(key)) { seen.add(key); result.push(o); }
      }
    }
    return result;
  }, [batches]);

  const stopped = isSellingStopped(continueSelling);
  const selling = isContinueSelling(continueSelling);

  /* Show up to 3 batches */
  const topBatches = batches.slice(0, 3);

  /* ── Category breadcrumb ── */
  const catBreadcrumb = [mainCat, cat1, cat2].filter(Boolean).join(" › ") || null;

  return (
    <Modal
      open={cartItemModalOpen}
      onClose={() => setCartItemModalOpen(false)}
      slotProps={{ backdrop: { style: { backgroundColor: "rgba(0,0,0,0.4)" } } }}
    >
      <div className="cid__panel">

        {/* ══ Header — always visible immediately ══ */}
        <div className="cid__hdr">
          <div className="cid__hdr-left">
            {productImage ? (
              <img src={productImage} alt={cartItemData?.productName} className="cid__hdr-img" />
            ) : (
              <div className="cid__hdr-img-ph">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
            )}
          </div>

          <div className="cid__hdr-right">
            <div className="cid__hdr-top">
              <span className="cid__hdr-eyebrow">PRODUCT DETAILS</span>
              <button className="cid__close" onClick={() => setCartItemModalOpen(false)} aria-label="Close">✕</button>
            </div>
            <h2 className="cid__name">
              {cartItemData?.productName}
              {cartItemData?.variantName ? ` · ${cartItemData.variantName}` : ""}
            </h2>
            {brand && <span className="cid__brand">{brand}</span>}
            <div className="cid__meta-row">
              {isBundle              && <span className="cid__tag cid__tag--bundle">Bundle</span>}
              {isWeighingBarcode     && <span className="cid__tag cid__tag--scale">Scale</span>}
              {hasOffer              && <span className="cid__tag cid__tag--offer">Offer</span>}
              {overSold > 0          && <span className="cid__tag cid__tag--oversold">Oversold</span>}
              {stopped               && <span className="cid__tag cid__tag--stopped">Selling Stopped</span>}
              {selling && !stopped   && <span className="cid__tag cid__tag--active">Active</span>}
              {sellOutOfStock === "1" && <span className="cid__tag cid__tag--oos">Sell OOS</span>}
            </div>
          </div>
        </div>

        {/* ══ Body ══ */}
        {loading ? (
          <div className="cid__loading-body">
            <div className="cid__spinner" />
            <span className="cid__loading-text">Fetching product details…</span>
          </div>
        ) : apiError ? (
          <div className="cid__loading-body">
            <span className="cid__loading-text" style={{ color: "#ef4444" }}>
              Could not load product data. Please try again.
            </span>
          </div>
        ) : (
          <div className="cid__body">

            {/* ── 1. Product facts ── */}
            <div className="cid__section">
              <div className="cid__sec-hdr">
                <span className="cid__sec-title">PRODUCT INFO</span>
              </div>
              <div className="cid__facts">
                <div className="cid__fact">
                  <span className="cid__fl">Barcode</span>
                  <span className="cid__fv cid__fv--mono">{na(cartItemData?.gtin)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">HSN Code</span>
                  <span className="cid__fv cid__fv--mono">{na(hsnCode)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Status</span>
                  <span className="cid__fv">{na(productStatus)}</span>
                </div>
                <div className="cid__fact cid__fact--wide">
                  <span className="cid__fl">Category</span>
                  <span className="cid__fv">{na(catBreadcrumb)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Brand</span>
                  <span className="cid__fv">{na(brand)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Manufacturer</span>
                  <span className="cid__fv">{na(manufacturer)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Packaging</span>
                  <span className="cid__fv">{na(packagingType)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Selling Unit</span>
                  <span className="cid__fv">{na(sellingUnit || unitType)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Net Weight</span>
                  <span className="cid__fv">
                    {netWeight && weightUnit ? `${netWeight} ${weightUnit}` : "N/A"}
                  </span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Min Order Qty</span>
                  <span className="cid__fv">
                    {moq !== null ? `${moq}${moqUnit ? ` ${moqUnit}` : ""}` : "N/A"}
                  </span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Local Product</span>
                  <span className="cid__fv">{na(isLocal)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Specification</span>
                  <span className="cid__fv">{na(specification)}</span>
                </div>
                <div className="cid__fact cid__fact--wide">
                  <span className="cid__fl">Description</span>
                  <span className="cid__fv cid__fv--desc">{na(description)}</span>
                </div>
              </div>
            </div>

            {/* ── 2. GST strip ── */}
            <div className="cid__gst-strip">
              <span className="cid__gst-label">GST</span>
              <div className="cid__gst-chips">
                {igst !== null
                  ? <span className="cid__gst-chip">IGST {igst}%</span>
                  : <span className="cid__gst-chip cid__gst-chip--na">IGST N/A</span>
                }
                {cgst !== null
                  ? <span className="cid__gst-chip">CGST {cgst}%</span>
                  : <span className="cid__gst-chip cid__gst-chip--na">CGST N/A</span>
                }
                {sgst !== null
                  ? <span className="cid__gst-chip">SGST {sgst}%</span>
                  : <span className="cid__gst-chip cid__gst-chip--na">SGST N/A</span>
                }
                {cess !== null && <span className="cid__gst-chip cid__gst-chip--cess">CESS {cess}%</span>}
              </div>
            </div>

            {/* ── 3. Selling policy (IMS top-level) ── */}
            <div className="cid__section">
              <div className="cid__sec-hdr">
                <span className="cid__sec-title">SELLING POLICY</span>
              </div>
              <div className="cid__facts">
                <div className="cid__fact">
                  <span className="cid__fl">Continue Selling</span>
                  {continueSelling !== null ? (
                    <span className={`cid__pill ${stopped ? "cid__pill--no" : "cid__pill--yes"}`}>
                      {stopped ? "No" : "Yes"}
                    </span>
                  ) : (
                    <span className="cid__fv">N/A</span>
                  )}
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Oversell Limit</span>
                  <span className={`cid__fv ${overSold > 0 ? "cid__fv--warn" : ""}`}>
                    {overSold !== null ? (overSold > 0 ? overSold : "None") : "N/A"}
                  </span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Threshold</span>
                  <span className="cid__fv">{na(threshHold)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Reorder Point</span>
                  <span className="cid__fv">{na(reorderPoint)}</span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Reorder Qty</span>
                  <span className="cid__fv">
                    {reorderQty !== null
                      ? `${reorderQty}${reorderQtyType ? ` ${reorderQtyType}` : ""}`
                      : "N/A"}
                  </span>
                </div>
                <div className="cid__fact">
                  <span className="cid__fl">Margin</span>
                  {marginValue !== null ? (
                    <span className="cid__fv cid__fv--margin">
                      {marginValue}{marginType === "%" ? "%" : ` ${marginType || ""}`}
                      {marginBasedOn ? <em className="cid__margin-basis"> on {marginBasedOn}</em> : null}
                    </span>
                  ) : (
                    <span className="cid__fv">N/A</span>
                  )}
                </div>
                {/* B2B price shown in lookup mode; cart mode shows it inside the snap card */}
                {!isCartMode && (
                  <div className="cid__fact">
                    <span className="cid__fl">B2B Price</span>
                    <span className="cid__fv cid__fv--b2b">{fmt(b2bSellingPrice)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── 4. Cart snapshot (cart mode only) ── */}
            {isCartMode && (
              <div className="cid__cart-snap">
                <div className="cid__snap-hdr">
                  <span className="cid__snap-label">IN CART</span>
                  {cartItemData?.batchNo && (
                    <span className="cid__snap-batch">{cartItemData.batchNo}</span>
                  )}
                  {cartItemData?.expiryDate && (
                    <span className="cid__snap-expiry">Exp {fmtDate(cartItemData.expiryDate)}</span>
                  )}
                  <span className={`cid__snap-status ${
                    cartItemData?.inventoryStatus === "IN_STOCK"
                      ? "cid__snap-status--ok"
                      : "cid__snap-status--bad"
                  }`}>
                    {cartItemData?.inventoryStatus === "IN_STOCK"
                      ? "Ready to bill"
                      : (cartItemData?.inventoryStatus || "—")}
                  </span>
                </div>
                <div className="cid__snap-grid">
                  <div className="cid__snap-cell">
                    <span className="cid__fl">Qty</span>
                    <span className="cid__snap-val">{cartItemData?.quantity ?? "N/A"}</span>
                  </div>
                  <div className="cid__snap-cell">
                    <span className="cid__fl">MRP</span>
                    <span className="cid__snap-val">{fmt(cartItemData?.mrp)}</span>
                  </div>
                  <div className="cid__snap-cell">
                    <span className="cid__fl">Selling</span>
                    <span className="cid__snap-val cid__snap-val--sp">{fmt(cartItemData?.sellingPrice)}</span>
                  </div>
                  <div className="cid__snap-cell">
                    <span className="cid__fl">Total</span>
                    <span className="cid__snap-val cid__snap-val--total">{fmt(cartItemData?.subTotal)}</span>
                  </div>
                  <div className="cid__snap-cell">
                    <span className="cid__fl">B2B</span>
                    <span className="cid__snap-val cid__snap-val--b2b">{fmt(b2bSellingPrice)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── 5. Inventory batches ── */}
            <div className="cid__section">
              <div className="cid__sec-hdr">
                <span className="cid__sec-title">INVENTORY BATCHES</span>
                {batches.length > 3 && (
                  <span className="cid__count-pill">{batches.length} total</span>
                )}
              </div>

              {topBatches.length === 0 ? (
                <p className="cid__empty">No inventory batches found.</p>
              ) : (
                <div className="cid__batch-list">
                  {topBatches.map((batch, idx) => {
                    const bId      = batch?.batchId || batch?.inventoryId || `Batch ${idx + 1}`;
                    const avail    = batch?.availableUnits;
                    const qty      = batch?.quantity;
                    const bMrp     = batch?.mrp;
                    const bSp      = batch?.sellingPrice;
                    const bPp      = batch?.purchasePrice;
                    const bMargin  = batch?.purchaseMargin;
                    const bExp     = batch?.expiryDate;
                    const bOffer   = batch?.haveOffer;
                    const bOsQty   = batch?.overSoldQuantity;
                    const bB2b     = batch?.b2bSellingPrice;
                    const bFreebie = batch?.freebie;
                    const bUnit    = batch?.inventorySellingUnit;
                    const storage  = batch?.storageName || batch?.storageId || null;
                    const poRef    = batch?.requestNumber
                      ? `${batch.requestNumberType || "Ref"}: ${batch.requestNumber}`
                      : null;
                    const isActive = isCartMode && cartItemData?.batchNo === bId;

                    const stockVal = avail ?? qty;

                    return (
                      <div key={idx} className={`cid__batch-row${isActive ? " cid__batch-row--active" : ""}`}>
                        {/* Batch header row */}
                        <div className="cid__batch-id-row">
                          {isActive  && <span className="cid__active-dot" />}
                          <span className="cid__batch-id">{bId}</span>
                          {bOffer    && <span className="cid__mini-pill cid__mini-pill--offer">Offer</span>}
                          {bFreebie  && <span className="cid__mini-pill cid__mini-pill--freebie">Freebie</span>}
                          {bOsQty > 0 && <span className="cid__mini-pill cid__mini-pill--os">OS: {bOsQty}</span>}
                          {bExp && <span className="cid__batch-exp">Exp {fmtDate(bExp)}</span>}
                        </div>

                        {/* Price / stock row — all fields always shown */}
                        <div className="cid__batch-nums">
                          <span className="cid__bnum">
                            <span className="cid__fl">Stock</span>
                            <span className={`cid__bval ${stockVal > 0 ? "cid__bval--in" : stockVal === 0 ? "cid__bval--out" : ""}`}>
                              {stockVal !== null && stockVal !== undefined
                                ? `${stockVal}${bUnit ? ` ${bUnit}` : ""}`
                                : "N/A"}
                            </span>
                          </span>
                          <span className="cid__bnum">
                            <span className="cid__fl">MRP</span>
                            <span className="cid__bval">{fmt(bMrp)}</span>
                          </span>
                          <span className="cid__bnum">
                            <span className="cid__fl">Selling</span>
                            <span className="cid__bval cid__bval--sp">{fmt(bSp)}</span>
                          </span>
                          <span className="cid__bnum">
                            <span className="cid__fl">Purchase</span>
                            <span className="cid__bval">{fmt(bPp)}</span>
                          </span>
                          <span className="cid__bnum">
                            <span className="cid__fl">Margin</span>
                            <span className="cid__bval cid__bval--margin">
                              {bMargin !== null && bMargin !== undefined
                                ? `${parseFloat(bMargin).toFixed(1)}%`
                                : "N/A"}
                            </span>
                          </span>
                          <span className="cid__bnum">
                            <span className="cid__fl">B2B</span>
                            <span className="cid__bval cid__bval--b2b">{fmt(bB2b)}</span>
                          </span>
                          <span className="cid__bnum">
                            <span className="cid__fl">Offer</span>
                            <span className={`cid__pill ${bOffer ? "cid__pill--yes" : "cid__pill--no"}`}>
                              {bOffer ? "Yes" : "No"}
                            </span>
                          </span>
                        </div>

                        {/* Storage / PO reference */}
                        <div className="cid__batch-meta">
                          <span className="cid__batch-meta-item">
                            📦 {storage || "N/A"}
                          </span>
                          <span className="cid__batch-meta-item">
                            🔖 {poRef || "N/A"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── 6. Offers ── */}
            {hasOffer && offerList.length > 0 && (
              <div className="cid__section cid__section--offer">
                <div className="cid__sec-hdr">
                  <span className="cid__sec-title">OFFERS</span>
                </div>
                <div className="cid__offer-list">
                  {offerList.map((offer, idx) => (
                    <div key={idx} className="cid__offer-row">
                      <span className="cid__offer-dot" />
                      <span className="cid__offer-name">
                        {offer?.offerName || offer?.name || `Offer ${idx + 1}`}
                      </span>
                      {offer?.discountValue != null && (
                        <span className="cid__offer-val">
                          {offer?.discountType === "PERCENTAGE"
                            ? `${offer.discountValue}% off`
                            : `₹${offer.discountValue} off`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {/* ══ Footer ══ */}
        <div className="cid__footer">
          <PosCustomButton
            title="Done"
            variant="blue-d"
            radius="10px"
            p="10px 22px"
            fontSize="13px"
            fontWeight={600}
            onClickFunction={() => setCartItemModalOpen(false)}
          />
        </div>

      </div>
    </Modal>
  );
};

export default CartItemDrawer;
